import { initUiErrorReporting, initUiI18n, initUiToast } from "@cynthai/ui";
import * as dayjs from "dayjs";

import "./index.css";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import utc from "dayjs/plugin/utc";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import "./core/lib/api-client";
import "@/app/modals/registry.gen";
import { initAuthRouter } from "@/core/lib/auth-router";
import {
  handleChunkLoadError,
  scheduleChunkGuardReset,
} from "@/core/lib/chunk-reload";
import { applyEnvironmentFavicon } from "@/core/lib/favicon";
import {
  getFaro,
  initializeFaro,
  trackRouteViews,
} from "@/core/lib/observability";
import { router } from "@/core/lib/router";
import { addToast } from "@/core/lib/toast";
import MainContextProvider from "@/main-context";
import {
  getLocale,
  locales,
  localStorageKey,
  setLocale,
} from "@/paraglide/runtime";

if (import.meta.env.VITE_ENABLE_SCAN === "true") {
  const { scan } = await import("react-scan");
  scan({ enabled: true });
}

window.addEventListener("error", (event) => {
  if (event.error && handleChunkLoadError(event.error, "window")) {
    event.preventDefault();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (event.reason && handleChunkLoadError(event.reason, "window")) {
    event.preventDefault();
  }
});

window.addEventListener("load", () => {
  scheduleChunkGuardReset();
});

// Automatic language detection with fallback to stored user preference
const browserLanguage = navigator.language.split("-")[0] as string;
const supportedLanguages: ReadonlySet<string> = new Set(locales);

let storedLanguage: string | null;
try {
  storedLanguage = localStorage.getItem(localStorageKey);
} catch {
  storedLanguage = null;
}

const defaultLanguage: string =
  storedLanguage && supportedLanguages.has(storedLanguage)
    ? storedLanguage
    : supportedLanguages.has(browserLanguage)
      ? browserLanguage
      : "en";

// Pass reload: false; this is purely initialization before the React tree renders.
// A reload here would be unnecessary and causes a visible flash/stutter on first
void setLocale(defaultLanguage as (typeof locales)[number], { reload: false });

// Sync the design-system runtime to the app's active locale.
initUiI18n({ getLocale, setLocale });

// Let design-system components surface validation errors through app toasts.
initUiToast(addToast);

// Let api-client send an unrecoverable session back to sign-in without
// importing the router singleton.
initAuthRouter(router);

dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(utc);

applyEnvironmentFavicon();
initializeFaro();
trackRouteViews(router);

// Let design-system error boundaries report through the app's observability stack.
initUiErrorReporting((error, context) => {
  getFaro()?.api.pushError(error, { context });
});

const rootElement = document.getElementById("root");

if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <MainContextProvider />
    </StrictMode>,
  );
}
